import { requireRuntimeApi } from "./state.ts";
import { makeModelLabel } from "./format.ts";
import { radioEventParams, trackEvent } from "./analytics.ts";
import { DEFAULT_DRIVER_SET, QUANSHENG_UNOFFICIAL_DRIVER_SET } from "../python-sources.ts";
import type { UiContext } from "../types/ui-context.js";
import type { CatalogRadio, LoadedImage } from "../runtime-rpc.ts";
import type { RadioMetadata } from "./channel-values.ts";
import type { RadioSessionHandle } from "./state.ts";

/** Another name a catalog radio is sold under. */
type RadioAlias = NonNullable<CatalogRadio["aliases"]>[number];

const LAST_RADIO_COOKIE = "webchirp_last_radio";
const RADIO_SEARCH_MAX_RESULTS = 50;
const NO_RADIO_SELECTED_TEXT = "No radio model selected";
// Suggestions are the only way to choose a radio, so each one needs an id for
// the combobox's aria-activedescendant to point a screen reader at.
const RADIO_SEARCH_OPTION_ID_PREFIX = "radio-search-option-";

// Radio selection: the free-text search box with its autocomplete list, the
// sidebar readout naming the radio in use, the "last radio" cookie, and the
// metadata load that follows a selection. Search is the only way to pick a
// radio, so nothing is selected until the user picks one (or the cookie
// restores their last). Owns the search-result state; the catalog and the
// selected entry live in the shared state because export/upload read them.
export function createRadioCatalog(ctx: UiContext) {
  const { dom, state, log, actions } = ctx;
  let searchMatches: CatalogRadio[] = [];
  let searchActiveIndex = -1;
  // Overrides the readout while the catalog is loading or failed to load, so a
  // cold start does not claim the user simply has not chosen a radio yet.
  let catalogStatusText = "";

  function setCookie(name: string, value: string, maxAgeSeconds = 31536000): void {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
  }

  function getCookie(name: string): string {
    const prefix = `${name}=`;
    const parts = String(document.cookie || "").split(";").map((v) => v.trim());
    for (const part of parts) {
      if (part.startsWith(prefix)) {
        return decodeURIComponent(part.slice(prefix.length));
      }
    }
    return "";
  }

  function persistSelectedRadioCookie() {
    if (!state.selectedRadio) {
      return;
    }
    const value = JSON.stringify({
      make: state.selectedRadio.vendor,
      key: state.selectedRadio.key,
    });
    setCookie(LAST_RADIO_COOKIE, value);
  }

  function restoreSelectedRadioCookie() {
    const raw = getCookie(LAST_RADIO_COOKIE);
    if (!raw) {
      return false;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false;
    }
    const make = String(parsed?.make || "");
    const key = String(parsed?.key || "");
    if (!make || !key) {
      return false;
    }
    const restored = state.radioCatalog.find((r) => r.vendor === make && r.key === key);
    if (!restored) {
      return false;
    }
    commitSelectedRadio(restored);
    trackRadioSelected(restored, "restored");
    log.logDebug(
      `RADIO RESTORE ${makeModelLabel(restored)} (${restored.module}.${restored.className})`,
    );
    return true;
  }

  // Report which radio a user landed on and how they got there.
  function trackRadioSelected(radio: CatalogRadio | null, method: string): void {
    if (!radio) {
      return;
    }
    trackEvent("radio_selected", {
      ...radioEventParams(radio),
      method,
    });
  }

  // Alternate vendor/model identities the same driver is sold under (CHIRP's
  // ALIASES). Searching these is what replaces browsing a make dropdown: a
  // Retevis RT5R owner has no other way to discover it is a Baofeng UV-5R
  // driver, because the catalog lists the entry under Baofeng only.
  function radioAliasIdentities(radio: CatalogRadio): RadioAlias[] {
    return (radio.aliases || []).filter(
      (alias) => alias.vendor !== radio.vendor || alias.model !== radio.model,
    );
  }

  function aliasLabel(alias: RadioAlias): string {
    return `${alias.vendor} ${alias.model}${alias.variant ? ` ${alias.variant}` : ""}`;
  }

  // The radio's own searchable text, without its aliases.
  function primaryHaystack(radio: CatalogRadio): string {
    return `${makeModelLabel(radio)} ${radio.className}`.toLowerCase();
  }

  function matchesAllTokens(haystack: string, tokens: readonly string[]): boolean {
    return tokens.every((token) => haystack.includes(token));
  }

  // Match a radio against a search query; every whitespace-separated token must
  // appear somewhere in its own or an alias identity's text (case-insensitive).
  function radioMatchesFilter(radio: CatalogRadio, tokens: readonly string[]): boolean {
    if (tokens.length === 0) {
      return true;
    }
    const primary = primaryHaystack(radio);
    if (matchesAllTokens(primary, tokens)) {
      return true;
    }
    return radioAliasIdentities(radio).some((alias) =>
      matchesAllTokens(`${primary} ${aliasLabel(alias)}`.toLowerCase(), tokens),
    );
  }

  // The alias that explains why a radio matched, or null when its own
  // vendor/model already covers the query. Used to label the suggestion, so a
  // search for "retevis" does not return a list of unexplained Baofengs.
  function matchedAlias(radio: CatalogRadio, tokens: readonly string[]): RadioAlias | null {
    const primary = primaryHaystack(radio);
    if (tokens.length === 0 || matchesAllTokens(primary, tokens)) {
      return null;
    }
    return (
      radioAliasIdentities(radio).find((alias) =>
        matchesAllTokens(`${primary} ${aliasLabel(alias)}`.toLowerCase(), tokens),
      ) || null
    );
  }

  // Catalog entries matching a free-text search query.
  function matchingRadios(query: string): CatalogRadio[] {
    const tokens = searchTokens(query);
    if (tokens.length === 0) {
      return [];
    }
    return state.radioCatalog.filter((radio) => radioMatchesFilter(radio, tokens));
  }

  function searchTokens(query: string): string[] {
    return String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
  }

  // Whether another catalog entry wears the same "<Make> <Model>" text. When
  // one does, the name alone cannot say which driver Connect / Load / Save will
  // act on, so the class is the only thing that can.
  function hasAmbiguousLabel(radio: CatalogRadio): boolean {
    const label = makeModelLabel(radio);
    return state.radioCatalog.some(
      (other) => other.key !== radio.key && makeModelLabel(other) === label,
    );
  }

  // How a radio is named anywhere in this module — the suggestion rows and the
  // persistent readout share it, so the two cannot disagree about which entry
  // a name refers to.
  function radioLabel(radio: CatalogRadio, isAmbiguous: boolean): string {
    const label = makeModelLabel(radio);
    return isAmbiguous ? `${label} (${radio.className})` : label;
  }

  // Fill one suggestion row. The name and the alias note are separate elements
  // so the narrow sidebar can stack them instead of ellipsising the name away.
  function fillRadioSearchOption(
    li: HTMLLIElement,
    radio: CatalogRadio,
    hasDuplicateLabel: boolean,
    alias: RadioAlias | null,
  ): void {
    const nameEl = document.createElement("span");
    nameEl.className = "radio-search-name";
    nameEl.textContent = radioLabel(radio, hasDuplicateLabel);
    li.appendChild(nameEl);
    if (!alias) {
      return;
    }
    const aliasEl = document.createElement("span");
    aliasEl.className = "radio-search-alias";
    aliasEl.textContent = `also sold as ${aliasLabel(alias)}`;
    li.appendChild(aliasEl);
  }

  // Name the radio the rest of the app is working with. This readout is the
  // only indication of the current selection now that the make/model dropdowns
  // are gone, so it renders after every path that assigns state.selectedRadio.
  // It names the radio the way its owner would, and the driver class only where
  // that name is ambiguous — the search box empties after a selection, so this
  // is the one place left that can resolve two entries sharing a name.
  function renderSelectedRadio() {
    const radio = state.selectedRadio;
    dom.radioSelectionEl.classList.toggle("is-empty", !radio);
    if (!radio) {
      dom.radioSelectionNameEl.textContent = catalogStatusText || NO_RADIO_SELECTED_TEXT;
      return;
    }
    dom.radioSelectionNameEl.textContent = radioLabel(radio, hasAmbiguousLabel(radio));
  }

  // The single way this module adopts a radio. Selection has three entry
  // points — a suggestion, the cookie, an image naming its own driver — and the
  // serial actions are gated on there being a radio at all, so a path that
  // assigned state.selectedRadio without re-gating them left Connect, Load and
  // Save disabled behind a populated readout. The runtime session moves with
  // the selection: opened here for the radio, or adopted when an image load
  // already opened one for the driver it named.
  function commitSelectedRadio(radio: CatalogRadio, adoptedSessionId = ""): void {
    clearRadioFilter();
    state.selectedRadio = radio;
    ctx.session.open(radio, adoptedSessionId);
    renderSelectedRadio();
    actions.updateSerialActionState();
  }

  function hideRadioSearchResults() {
    searchMatches = [];
    searchActiveIndex = -1;
    dom.radioSearchResultsEl.hidden = true;
    dom.radioSearchResultsEl.innerHTML = "";
    dom.radioSearchEl.setAttribute("aria-expanded", "false");
    dom.radioSearchEl.removeAttribute("aria-activedescendant");
  }

  // Point the combobox at the highlighted suggestion, or at nothing when the
  // list has none to highlight.
  function syncRadioSearchActiveDescendant() {
    if (searchActiveIndex < 0) {
      dom.radioSearchEl.removeAttribute("aria-activedescendant");
      return;
    }
    dom.radioSearchEl.setAttribute(
      "aria-activedescendant",
      `${RADIO_SEARCH_OPTION_ID_PREFIX}${searchActiveIndex}`,
    );
  }

  // Clear the search box and close its suggestion list. The box is a way to
  // change the selection, not a display of it, so it empties after every
  // selection and the readout above it carries the answer.
  function clearRadioFilter() {
    dom.radioSearchEl.value = "";
    hideRadioSearchResults();
  }

  // Render the autocomplete dropdown for the current search box contents.
  function renderRadioSearchResults() {
    const query = String(dom.radioSearchEl.value || "").trim();
    if (!query) {
      hideRadioSearchResults();
      return;
    }
    const tokens = searchTokens(query);
    const matches = matchingRadios(query);
    searchMatches = matches.slice(0, RADIO_SEARCH_MAX_RESULTS);
    searchActiveIndex = searchMatches.length > 0 ? 0 : -1;
    dom.radioSearchResultsEl.innerHTML = "";

    if (matches.length === 0) {
      const li = document.createElement("li");
      li.classList.add("radio-search-empty");
      li.textContent = "No matching radios";
      dom.radioSearchResultsEl.appendChild(li);
    } else {
      const labelCounts = new Map();
      for (const radio of searchMatches) {
        const label = makeModelLabel(radio);
        labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
      }
      searchMatches.forEach((radio, index) => {
        const li = document.createElement("li");
        li.setAttribute("role", "option");
        li.id = `${RADIO_SEARCH_OPTION_ID_PREFIX}${index}`;
        li.dataset.index = String(index);
        const hasDuplicateLabel = (labelCounts.get(makeModelLabel(radio)) || 0) > 1;
        fillRadioSearchOption(li, radio, hasDuplicateLabel, matchedAlias(radio, tokens));
        li.setAttribute("aria-selected", index === searchActiveIndex ? "true" : "false");
        if (index === searchActiveIndex) {
          li.classList.add("is-active");
        }
        dom.radioSearchResultsEl.appendChild(li);
      });
      if (matches.length > searchMatches.length) {
        const li = document.createElement("li");
        li.classList.add("radio-search-more");
        li.textContent = `${matches.length - searchMatches.length} more — keep typing to narrow down`;
        dom.radioSearchResultsEl.appendChild(li);
      }
    }

    dom.radioSearchResultsEl.hidden = false;
    dom.radioSearchEl.setAttribute("aria-expanded", "true");
    syncRadioSearchActiveDescendant();
  }

  // Move the keyboard highlight in the suggestion list by delta and keep it in view.
  function moveRadioSearchActive(delta: number): void {
    if (searchMatches.length === 0) {
      return;
    }
    const count = searchMatches.length;
    searchActiveIndex = (searchActiveIndex + delta + count) % count;
    const items = dom.radioSearchResultsEl.querySelectorAll("li[role='option']");
    items.forEach((li, index) => {
      li.classList.toggle("is-active", index === searchActiveIndex);
      li.setAttribute("aria-selected", index === searchActiveIndex ? "true" : "false");
    });
    syncRadioSearchActiveDescendant();
    items[searchActiveIndex]?.scrollIntoView({ block: "nearest" });
  }

  // Apply a suggestion: name it in the readout and load the radio.
  function applyRadioSearchSelection(radio: CatalogRadio | null | undefined): void {
    if (!radio) {
      return;
    }
    // The search box is disabled while a radio transfer runs; this covers a
    // pick that reaches here anyway, from a list left open when it started.
    if (ctx.serial.isCloneRunning()) {
      hideRadioSearchResults();
      return;
    }
    commitSelectedRadio(radio);
    trackRadioSelected(radio, "search");
    log.logDebug(
      `RADIO SELECT ${makeModelLabel(radio)} (${radio.module}.${radio.className})`,
    );
    reloadForSelectedRadio();
  }

  // Shared side effects after the selected radio changes.
  function reloadForSelectedRadio() {
    actions.updateSerialActionState();
    persistSelectedRadioCookie();
    ctx.table.clearInvalidHighlights();
    ctx.settings.clearInvalid();
    // The session for the selection just committed. Reselecting the radio the
    // current session is for keeps that session (web/js/ui/radio-session.ts),
    // so one that has already loaded needs no new runtime calls and keeps the
    // user's edits, and one still loading will apply its result when it lands.
    const session = ctx.session.current();
    if (session?.loaded) {
      ctx.table.render();
      return;
    }
    if (session?.loading) {
      return;
    }
    if (session) {
      session.loading = true;
    }
    Promise.all([
      fetchRadioMetadata(session),
      ctx.settings.fetchForSession(session),
    ])
      .then(([metadata, settingsState]) => {
        // A response for a session the selection has since moved on from is
        // dropped whole: the session was closed when it stopped being current.
        if (!ctx.session.isCurrent(session)) {
          return;
        }
        // Commit the schema and settings together. If one request finishes
        // before the other, the editor keeps showing the previous radio's
        // complete state rather than a temporary mixture of both radios.
        applyRadioMetadata(metadata);
        ctx.settings.applyLoadedState(settingsState);
        ctx.session.markLoaded(session);
        ctx.table.render();
      })
      .catch((error) => {
        if (ctx.session.isCurrent(session)) {
          log.reportActionError("Metadata load", error);
        }
      })
      .finally(() => {
        if (session) {
          session.loading = false;
        }
      });
  }

  // Say why no radio is named yet while the catalog is unavailable ("Loading…",
  // "Unavailable"), instead of the readout's ordinary empty text.
  function setRadioSelectPlaceholder(label: string): void {
    catalogStatusText = String(label || "");
    renderSelectedRadio();
  }

  // Called once the catalog has loaded: the readout drops the loading text and
  // the search box gets a placeholder that says how much there is to search.
  function refreshCatalog() {
    catalogStatusText = "";
    const count = state.radioCatalog.length;
    dom.radioSearchEl.placeholder = count > 0
      ? `Search ${count} radios…`
      : "No radio definitions available";
    if (state.selectedRadio && !state.radioCatalog.includes(state.selectedRadio)) {
      state.selectedRadio = null;
      ctx.session.close();
    }
    renderSelectedRadio();
    actions.updateSerialActionState();
  }

  // Select the catalog entry for a driver without going through the search box
  // (used when a loaded image identifies its own driver, in which case the
  // session the load opened for that driver is adopted along with it).
  function selectRadioByDriver(moduleName: string, className: string, adoptedSessionId = ""): boolean {
    const target = state.radioCatalog.find(
      (r) => r.module === moduleName && r.className === className,
    );
    if (!target) {
      return false;
    }
    commitSelectedRadio(target, adoptedSessionId);
    persistSelectedRadioCookie();
    return true;
  }

  // Preselect the radio a ?radio=<catalog key> link names, so a per-model page
  // (web/radios/, built by scripts/build-model-pages.ts) can hand its visitor
  // an app already pointed at the radio the page is about. The key is the
  // catalog's own module:class, which is what those pages are generated from,
  // so an unknown one means the driver went away between the two -- fall
  // through to the cookie rather than clearing what the user had.
  // The default reads the live URL; the argument is what lets a test drive it.
  // Optional all the way down because the headless DOM the tests install has no
  // location, and a boot-time throw here would take the whole app with it.
  function selectRadioByLinkParam(search = window.location?.search || "") {
    const key = new URLSearchParams(search).get("radio");
    if (!key) {
      return false;
    }
    const target = state.radioCatalog.find((r) => r.key === key);
    if (!target) {
      log.logDebug(`RADIO LINK no catalog entry for ${key}`);
      return false;
    }
    commitSelectedRadio(target);
    trackRadioSelected(target, "link");
    persistSelectedRadioCookie();
    log.logDebug(`RADIO LINK ${makeModelLabel(target)} (${target.module}.${target.className})`);
    return true;
  }

  // Select the radio a loaded image identified, adopting the session the load
  // opened for it so the image it holds is the one the editor works on.
  function selectRadioByDetectedImage(
    loaded: Pick<LoadedImage, "sessionId" | "module" | "className" | "vendor" | "model">,
  ) {
    const sessionId = String(loaded.sessionId || "");
    if (selectRadioByDriver(loaded.module, loaded.className, sessionId)) {
      trackRadioSelected(state.selectedRadio, "image");
      return true;
    }
    const vendor = String(loaded.vendor || "");
    const model = String(loaded.model || "");
    const fallback = state.radioCatalog.find(
      (r) =>
        r.module === loaded.module
        && r.vendor === vendor
        && r.model === model,
    );
    if (!fallback) {
      return false;
    }
    commitSelectedRadio(fallback, sessionId);
    trackRadioSelected(fallback, "image");
    persistSelectedRadioCookie();
    return true;
  }

  // The column schema for a session's radio, or the empty schema for none.
  async function fetchRadioMetadata(session: RadioSessionHandle | null): Promise<RadioMetadata> {
    if (!session) {
      return { headers: [], columns: {} };
    }
    const sessionId = await ctx.session.idOf(session);
    const metadata = await requireRuntimeApi(state).getRadioMetadata({ sessionId });
    return metadata || { headers: [], columns: {} };
  }

  function applyRadioMetadata(metadata: RadioMetadata): void {
    const previous = state.radioMetadata;
    state.radioMetadata = metadata;
    state.currentHeaders = state.radioMetadata.headers?.length
      ? state.radioMetadata.headers
      : state.currentHeaders;
    // Channels can predate the radio: repeaters imported before one was
    // picked, or a codeplug edited under a different driver. Their Power
    // labels belong to whatever schema built them, and this driver's preflight
    // rejects a label it does not publish, so move those onto this radio's
    // closest level rather than let the user meet the error one upload later.
    // The previous schema is what says how many watts each label stood for.
    void ctx.table.adoptPowerLevels(previous);
  }

  // Load the selected radio's CHIRP-derived column metadata from the Python
  // runtime, applied only if the selection's session is still the current one.
  async function loadSelectedRadioMetadata() {
    const session = ctx.session.current();
    const metadata = await fetchRadioMetadata(session);
    if (!ctx.session.isCurrent(session)) {
      return;
    }
    applyRadioMetadata(metadata);
  }

  function bindEvents() {
    // Keep switching links available even if catalog loading fails. Missing or
    // empty parameters select CHIRP; any other set offers a return to the root.
    const driverSet = new URLSearchParams(window.location?.search || "").get("drivers")
      || DEFAULT_DRIVER_SET;
    dom.useQuanshengDriversEl.hidden = driverSet === QUANSHENG_UNOFFICIAL_DRIVER_SET;
    dom.useChirpDriversEl.hidden = driverSet === DEFAULT_DRIVER_SET;
    // Non-CHIRP driver sets carry the stronger hardware-risk warning.
    const unofficial = driverSet !== DEFAULT_DRIVER_SET;
    dom.standardDriverWarningEl.hidden = unofficial;
    dom.unofficialDriverWarningEl.hidden = !unofficial;
    dom.sidebarWarningEl.classList.toggle("is-unofficial", unofficial);
    // Typing opens an autocomplete list of "<Make> <Model>" suggestions; the
    // (Pyodide-backed) metadata/settings load only happens once the user picks
    // a suggestion via keyboard or mouse.
    dom.radioSearchEl.addEventListener("input", () => {
      renderRadioSearchResults();
    });

    dom.radioSearchEl.addEventListener("focus", () => {
      renderRadioSearchResults();
    });

    dom.radioSearchEl.addEventListener("keydown", (event) => {
      const isOpen = !dom.radioSearchResultsEl.hidden;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (!isOpen) {
          renderRadioSearchResults();
          return;
        }
        moveRadioSearchActive(event.key === "ArrowDown" ? 1 : -1);
      } else if (event.key === "Enter") {
        if (isOpen && searchActiveIndex >= 0) {
          event.preventDefault();
          applyRadioSearchSelection(searchMatches[searchActiveIndex]);
        }
      } else if (event.key === "Escape") {
        if (isOpen) {
          event.stopPropagation();
          hideRadioSearchResults();
        }
      }
    });

    dom.radioSearchEl.addEventListener("blur", () => {
      // Delay so a click on a suggestion (which blurs the input) still lands.
      setTimeout(() => hideRadioSearchResults(), 150);
    });

    dom.radioSearchResultsEl.addEventListener("mousedown", (event) => {
      // Prevent the input blur so the click handler below sees the list open.
      event.preventDefault();
      const li = (event.target as Element).closest("li[role='option']") as HTMLLIElement | null;
      if (!li) {
        return;
      }
      const index = Number(li.dataset.index);
      applyRadioSearchSelection(searchMatches[index]);
    });
  }

  return {
    bindEvents,
    refreshCatalog,
    restoreSelectedRadioCookie,
    selectRadioByLinkParam,
    setRadioSelectPlaceholder,
    selectRadioByDetectedImage,
    loadSelectedRadioMetadata,
  };
}
